import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Eye, Loader2, Save, Send } from "lucide-react";

const LABELS = {
  full_name: "Full name", school: "School", bio: "Bio", title: "Campaign title", story: "Funding need",
  category: "Type of need", request_doc: "Supporting document", attestations: "Attestations",
  cover: "Profile photo", photos: "Photos", video: "Video pitch", payouts: "Stripe payouts",
};

export const Checklist = ({ checklist }) => {
  if (!checklist) return null;
  const required = Object.entries(checklist.items).filter(([, v]) => v.required);
  const optional = Object.entries(checklist.items).filter(([, v]) => !v.required);
  const done = required.filter(([, v]) => v.done).length;
  const Chip = ({ k, v }) => (
    <span
      data-testid={`checklist-${k}`}
      data-done={v.done}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset ${ v.done ? "bg-grass/12 text-grass ring-grass/30" : v.required ? "bg-sand text-ink ring-ink/15" : "bg-sand text-olive ring-ink/10" }`}
    >
      {v.done ? <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} /> : <Circle className="h-3 w-3" strokeWidth={2} />}
      {LABELS[k] || k}
    </span>
  );
  return (
    <div data-testid="builder-checklist" className="rounded-lg border border-border bg-sand p-5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-ink">Ready to submit?</span>
        <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive" data-testid="checklist-progress">
          {done} of {required.length} required
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">{required.map(([k, v]) => <Chip key={k} k={k} v={v} />)}</div>
      <p className="mt-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">Recommended, not required</p>
      <div className="mt-2 flex flex-wrap gap-2">{optional.map(([k, v]) => <Chip key={k} k={k} v={v} />)}</div>
    </div>
  );
};

export const BuilderActions = ({ campaignId, dirty, saving, isLive, onSave, onSubmit, resubmit }) => (
  <div className="flex flex-col gap-3 pt-2 sm:flex-row">
    <Link
      to={`/campaign/${campaignId}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="builder-preview-btn"
      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-ink px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
    >
      <Eye className="h-4 w-4" strokeWidth={1.75} /> Preview page
    </Link>
    <button
      type="button"
      data-testid="builder-save-btn"
      onClick={onSave}
      disabled={saving || !dirty}
      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-tangerine px-6 py-3.5 text-base font-semibold text-tangerine transition-colors hover:bg-tangerine hover:text-sand disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-tangerine"
    >
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.75} />}
      {isLive ? "Save edits for review" : "Save draft"}
    </button>
    {!isLive && (
      <button
        type="button"
        data-testid="builder-submit-btn"
        onClick={onSubmit}
        disabled={saving}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-yellow px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-yellow-dark disabled:opacity-60"
      >
        <Send className="h-4 w-4" strokeWidth={1.75} /> {resubmit ? "Resubmit for review" : "Submit for review"}
      </button>
    )}
  </div>
);
